import { useRef, useState } from "react";
import { extractErrorMessage } from "@/core/api/errors";
import { extractUploadConflict, useUploadOnboardingDocument } from "./query";
import {
  UPLOAD_ITEM_STATE,
  type OnboardingDocumentType,
  type UploadItem,
  type UploadItemState,
  type UploadConflict,
  type UploadConflictResolution,
} from "./types";

// Enough for everything an owner is likely to have to hand at once, and small enough that
// a serial queue still finishes while they are watching it.
export const MAX_FILES_PER_BATCH = 10;

/**
 * One dropzone's worth of uploading. Each kind of document has its own instance, so a
 * batch of reference data and a batch of pay plans keep their own queues, progress and
 * name-collision question.
 *
 * @param businessId whose library the files go to
 * @param documentType which of the two kinds this dropzone sends
 */
export const useDocumentBatchUpload = (
  businessId: string,
  documentType: OnboardingDocumentType,
) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [conflict, setConflict] = useState<UploadConflict | null>(null);
  // A collision has to be answered mid-run, so the runner waits on the modal: the resolver
  // for the open question lives here until the customer answers it.
  const conflictAnswer = useRef<
    ((resolution: UploadConflictResolution | null) => void) | null
  >(null);
  const uploadMutation = useUploadOnboardingDocument(businessId);

  const setStatus = (index: number, status: UploadItemState) =>
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, status } : item,
      ),
    );

  const askAboutConflict = (nameConflict: UploadConflict) =>
    new Promise<UploadConflictResolution | null>((resolve) => {
      conflictAnswer.current = resolve;
      setConflict(nameConflict);
    });

  const answerConflict = (resolution: UploadConflictResolution | null) => {
    setConflict(null);
    conflictAnswer.current?.(resolution);
    conflictAnswer.current = null;
  };

  // Serial rather than parallel: the API takes one file per request, a collision is a
  // question the customer has to answer about one file, and a queue that finishes in a
  // known order is one they can follow.
  const uploadAll = async (selected: File[]) => {
    const batch = selected.slice(0, MAX_FILES_PER_BATCH);
    if (!batch.length || isUploading) return;

    setItems(
      batch.map((file) => ({
        file,
        status: { state: UPLOAD_ITEM_STATE.pending },
      })),
    );
    setIsUploading(true);

    // What did not go up stays in the queue with the reason, so a retry does not re-send
    // what already arrived and the customer can still see what happened.
    const remaining: UploadItem[] = [];
    for (let index = 0; index < batch.length; index += 1) {
      const file = batch[index];
      let onConflict: UploadConflictResolution | undefined;

      for (;;) {
        setStatus(index, { state: UPLOAD_ITEM_STATE.uploading, progress: 0 });

        try {
          await uploadMutation.mutateAsync({
            file,
            documentType,
            onProgress: (progress) =>
              setStatus(index, {
                state: UPLOAD_ITEM_STATE.uploading,
                progress,
              }),
            onConflict,
          });
          setStatus(index, { state: UPLOAD_ITEM_STATE.done });
          break;
        } catch (error) {
          // A name collision is a question, not a failure. It is asked once per file:
          // the retry carries the answer, so a second 409 is a real error.
          const nameConflict = extractUploadConflict(error);
          if (nameConflict && !onConflict) {
            const resolution = await askAboutConflict(nameConflict);
            if (!resolution) {
              setStatus(index, { state: UPLOAD_ITEM_STATE.skipped });
              remaining.push({
                file,
                status: { state: UPLOAD_ITEM_STATE.skipped },
              });
              break;
            }
            onConflict = resolution;
            continue;
          }

          const failed: UploadItemState = {
            state: UPLOAD_ITEM_STATE.failed,
            message: extractErrorMessage(error).message,
          };
          setStatus(index, failed);
          remaining.push({ file, status: failed });
          break;
        }
      }
    }

    // Only what did not arrive stays on screen, with the reason. Everything that went up
    // is in the list of sent documents by now, so keeping it here would show it twice.
    setItems(remaining);
    setIsUploading(false);
  };

  const retry = () => uploadAll(items.map((item) => item.file));

  return {
    items,
    isUploading,
    conflict,
    uploadAll,
    retry,
    answerConflict,
  };
};
